import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileCheck, FileText, Image as ImageIcon, Eye, CheckCircle2, Inbox, Trophy } from "lucide-react";
import AppShell, { useAppUser } from "@/components/AppShell";
import BeltBar, { BELT_LABELS, degreeLabel } from "@/components/BeltBar";
import UserAvatar from "@/components/UserAvatar";
import { authApi, formatApiError } from "@/lib/api";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "—");
const fmtBr = (d) => (d ? d.split("-").reverse().join("/") : "—");

function StudentInfo({ b, testid }) {
  return (
    <>
      <UserAvatar name={b.student_name} photo={b.student_photo} birthDate={b.student_birth_date} size="md" testid={`${testid}-avatar`} />
      <div className="min-w-0 flex-1">
        <p className="font-display font-semibold" data-testid={`${testid}-student`}>
          {b.student_name}
          {b.student_age != null && <span className="text-zinc-500 font-normal text-sm"> · {b.student_age} anos</span>}
        </p>
        <div className="mt-1.5 flex items-center gap-2.5 flex-wrap">
          <BeltBar belt={b.student_belt || "branca"} degree={b.student_degree || 0} size="sm" testid={`${testid}-belt`} />
          <span className="font-mono text-[10px] tracking-wider text-zinc-500">
            FAIXA {(BELT_LABELS[b.student_belt || "branca"]).toUpperCase()} · {degreeLabel(b.student_degree || 0).toUpperCase()}
          </span>
          {b.unit_name && <span className="font-mono text-[10px] tracking-wider text-zinc-600">· {b.unit_name.toUpperCase()}</span>}
        </div>
      </div>
    </>
  );
}

export default function Validacoes() {
  const { user } = useAppUser() || {};
  const [bol, setBol] = useState(null);
  const [conq, setConq] = useState(null);
  const [validating, setValidating] = useState(null);
  const [opening, setOpening] = useState(null);

  const refresh = async () => {
    try {
      const [rb, rc] = await Promise.all([
        authApi().get("/boletins/pendentes"),
        authApi().get("/conquistas/pendentes"),
      ]);
      setBol(rb.data);
      setConq(rc.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBol({ pendentes: [], validados: [] });
      setConq({ pendentes: [], validados: [] });
    }
  };

  useEffect(() => {
    if (user) refresh();
  }, [user]);

  const openFile = async (b) => {
    setOpening(b.id);
    try {
      const r = await authApi().get(`/boletins/${b.id}/arquivo`);
      const res = await fetch(r.data.data_base64);
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), "_blank");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setOpening(null);
    }
  };

  const validate = async (kind, item) => {
    const label = kind === "boletim" ? `o boletim "${item.file_name}"` : `a conquista "${item.title}"`;
    if (!window.confirm(`Validar ${label} de ${item.student_name}?`)) return;
    setValidating(item.id);
    try {
      await authApi().post(`/${kind === "boletim" ? "boletins" : "conquistas"}/${item.id}/validar`);
      toast.success(`${kind === "boletim" ? "Boletim" : "Conquista"} de ${item.student_name} validado(a).`);
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setValidating(null);
    }
  };

  const renderBoletim = (b, i, pending) => (
    <div key={b.id} className="bg-[#1C1C23] border border-white/10 rounded-xl px-5 py-4 flex items-center gap-4 flex-wrap" data-testid={`boletim-${pending ? "pendente" : "validado"}-${i}`}>
      <StudentInfo b={b} testid={`boletim-${pending ? "p" : "v"}-${i}`} />
      <p className="font-mono text-[11px] text-zinc-500 w-full sm:w-auto">
        {b.file_name} · {fmtDate(b.uploaded_at)}
        {!pending && b.validated_by ? ` · por ${b.validated_by}` : ""}
      </p>
      <div className="flex items-center gap-2 shrink-0">
        {b.file_type === "application/pdf" ? <FileText size={20} className="text-crimson" /> : <ImageIcon size={20} className="text-gold" />}
        <button
          type="button"
          onClick={() => openFile(b)}
          disabled={opening === b.id}
          data-testid={`boletim-abrir-${pending ? "p" : "v"}-${i}`}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] tracking-widest border border-white/15 text-zinc-300 px-3 py-2 rounded-lg hover:border-gold/50 hover:text-gold transition-colors disabled:opacity-50"
        >
          <Eye size={13} /> {opening === b.id ? "ABRINDO..." : "VER"}
        </button>
        {pending && (
          <button
            type="button"
            onClick={() => validate("boletim", b)}
            disabled={validating === b.id}
            data-testid={`boletim-validar-${i}`}
            className="inline-flex items-center gap-1.5 font-display font-semibold text-xs bg-gold text-obsidian px-4 py-2 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-50"
          >
            <FileCheck size={14} /> {validating === b.id ? "Validando..." : "Validar"}
          </button>
        )}
      </div>
    </div>
  );

  const renderConquista = (c, i, pending) => (
    <div key={c.id} className="bg-[#1C1C23] border border-white/10 rounded-xl px-5 py-4 flex items-center gap-4 flex-wrap" data-testid={`conquista-${pending ? "pendente" : "validado"}-${i}`}>
      <StudentInfo b={c} testid={`conquista-${pending ? "p" : "v"}-${i}`} />
      <div className="flex items-center gap-2 min-w-0">
        <Trophy size={18} className="text-gold shrink-0" />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate" data-testid={`conquista-title-${pending ? "p" : "v"}-${i}`}>{c.title}</p>
          <p className="font-mono text-[11px] text-zinc-500">
            {c.competition ? `${c.competition} · ` : ""}{fmtBr(c.date)}
            {!pending && c.validated_by ? ` · por ${c.validated_by}` : ""}
          </p>
        </div>
      </div>
      {pending && (
        <button
          type="button"
          onClick={() => validate("conquista", c)}
          disabled={validating === c.id}
          data-testid={`conquista-validar-${i}`}
          className="inline-flex items-center gap-1.5 font-display font-semibold text-xs bg-gold text-obsidian px-4 py-2 rounded-full hover:bg-gold-amber transition-colors disabled:opacity-50 shrink-0"
        >
          <FileCheck size={14} /> {validating === c.id ? "Validando..." : "Validar"}
        </button>
      )}
    </div>
  );

  const Section = ({ label, color, items, empty, render, pending, testid }) => (
    <div>
      <p className={`font-mono text-xs tracking-[0.3em] ${color} mb-4`} data-testid={`${testid}-label`}>
        {label} ({items.length})
      </p>
      {items.length === 0 ? (
        <p className="text-sm text-zinc-600 border border-dashed border-white/15 rounded-xl py-6 text-center" data-testid={`${testid}-empty`}>{empty}</p>
      ) : (
        <div className="space-y-3" data-testid={`${testid}-list`}>{items.map((it, i) => render(it, i, pending))}</div>
      )}
    </div>
  );

  const loading = bol === null || conq === null;

  return (
    <AppShell active="validacoes">
      <section data-testid="validacoes-page">
        <h1 className="font-display text-2xl font-bold" data-testid="validacoes-title">Validações</h1>
        <p className="font-mono text-xs tracking-[0.3em] text-gold mt-2 mb-6">BOLETINS E CONQUISTAS DOS ALUNOS</p>

        {loading ? (
          <p className="text-zinc-500 text-sm">Carregando...</p>
        ) : (
          <div className="space-y-10">
            <div className="space-y-6">
              <h2 className="font-display font-semibold text-lg border-b border-white/10 pb-3">Boletins Escolares</h2>
              <Section label="AGUARDANDO VALIDAÇÃO" color="text-yellow-400" items={bol.pendentes} empty="Nenhum boletim aguardando validação." render={renderBoletim} pending testid="boletins-pendentes" />
              <Section label="VALIDADOS RECENTEMENTE" color="text-green-400" items={bol.validados} empty="Nenhum boletim validado ainda." render={renderBoletim} pending={false} testid="boletins-validados" />
            </div>

            <div className="space-y-6">
              <h2 className="font-display font-semibold text-lg border-b border-white/10 pb-3">Conquistas</h2>
              <Section label="AGUARDANDO VALIDAÇÃO" color="text-yellow-400" items={conq.pendentes} empty="Nenhuma conquista aguardando validação." render={renderConquista} pending testid="conquistas-pendentes" />
              <Section label="VALIDADAS RECENTEMENTE" color="text-green-400" items={conq.validados} empty="Nenhuma conquista validada ainda." render={renderConquista} pending={false} testid="conquistas-validadas" />
            </div>

            <p className="flex items-center gap-2 text-xs text-zinc-500" data-testid="validacoes-rule-note">
              <CheckCircle2 size={14} className="text-gold" /> Somente boletins e conquistas validados constam oficialmente no cadastro do aluno.
            </p>
          </div>
        )}
      </section>
    </AppShell>
  );
}
